import React, { useEffect, useRef } from "react";
import { NextSeo } from "next-seo";
import { ogImage } from "@/lib/og/url";
import Head from "next/head";
import Link from "next/link";
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote";
import {
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  ChevronRight,
  Clock,
  Check,
  ListOrdered,
  Minus,
  ShieldCheck,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/StaticAccordion";
import { SITE_URL } from "@/config/sitemapUrls";
import { blockMd, contentPath, readingMinutes } from "@/content";
import type {
  ContentBlock,
  ContentPage,
  ContentPick,
  ContentTable,
} from "@/content/types";
import type { ContentCard } from "@/utils/contentProps";
import { jsonLd } from "@/utils/jsonLd";
import CoverArt from "./CoverArt";
import { H2, MD_COMPONENTS, STEP_COMPONENTS, slugify } from "./prose";
import { Bars, Callout, Matrix, Verdicts } from "./RichBlocks";

// A blog or comparison page (content/types.ts). Answer first, then the
// article with an "On this page" rail, FAQ, sources and more reading.
// Markdown arrives serialized from getStaticProps (`mdx`, in mdSources order:
// one per md block, one per step of a steps block).

export const SECTION_LABEL = { blog: "Blog", compare: "Compare" } as const;

const PUBLISHER = {
  "@type": "Organization",
  name: "Jammable",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/android-chrome-512x512.png`,
  },
};

export const formatDate = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

const FAQ_ID = "questions";

function outline(page: ContentPage) {
  const items = page.blocks.flatMap((b) => {
    if (b.type === "md")
      return [...b.text.matchAll(/^## (.+)$/gm)].map((m) => m[1].trim());
    if (b.type === "steps") return [b.heading];
    if (
      (b.type === "table" ||
        b.type === "picks" ||
        b.type === "matrix" ||
        b.type === "verdicts" ||
        b.type === "bars") &&
      b.heading
    )
      return [b.heading];
    return [];
  });
  return [
    ...items.map((label) => ({
      label: label.replace(/[*_`]/g, ""),
      id: slugify(label),
    })),
    ...(page.faqs.length
      ? [{ label: "Questions people ask", id: FAQ_ID }]
      : []),
  ];
}

function Table({
  heading,
  table,
  verified,
}: {
  heading?: string;
  table: ContentTable;
  verified?: string;
}) {
  return (
    <>
      {heading && <H2>{heading}</H2>}
      <figure className="tw-mx-0 tw-my-6">
        <div className="tw-overflow-x-auto tw-rounded-[24px] tw-border-2 tw-border-solid tw-border-jam-ink tw-bg-white">
          <table className="tw-w-full tw-min-w-[560px] tw-border-collapse tw-text-left tw-text-sm">
            <thead>
              <tr className="tw-bg-jam-ink">
                {table.columns.map((c) => (
                  <th
                    key={c}
                    scope="col"
                    className="tw-whitespace-nowrap tw-px-4 tw-py-3.5 tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.08em] tw-text-white"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r) => {
                const ours = r[0] === "Jammable";
                return (
                  <tr
                    key={r[0]}
                    className={`tw-border-0 tw-border-t tw-border-solid tw-border-jam-line ${
                      ours ? "tw-bg-[#F8FFDD]" : "even:tw-bg-[#FCFAFF]"
                    }`}
                  >
                    {r.map((v, i) =>
                      i === 0 ? (
                        <th
                          key={i}
                          scope="row"
                          className="tw-whitespace-nowrap tw-px-4 tw-py-3.5 tw-font-extrabold tw-text-jam-ink"
                        >
                          {ours && (
                            <span
                              aria-hidden="true"
                              className="tw-mr-2 tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-jam-lime tw-ring-2 tw-ring-jam-ink"
                            />
                          )}
                          {v}
                        </th>
                      ) : (
                        <td
                          key={i}
                          className="tw-px-4 tw-py-3.5 tw-font-semibold tw-text-[#3d3a4b]"
                        >
                          {v}
                        </td>
                      ),
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(table.caption || verified) && (
          <figcaption className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-font-bold tw-text-jam-muted">
            {table.caption && <span>{table.caption}</span>}
            {verified && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-jam-lav tw-px-2.5 tw-py-1 tw-text-jam-deep">
                <ShieldCheck size={13} strokeWidth={2.6} />
                Checked on each tool&apos;s own site, {formatDate(verified)}
              </span>
            )}
          </figcaption>
        )}
      </figure>
    </>
  );
}

function PickCard({ pick, rank }: { pick: ContentPick; rank: number }) {
  const ours = pick.url.startsWith("/");
  const button =
    "tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-5 tw-py-2.5 tw-text-sm tw-font-extrabold tw-no-underline";
  return (
    <li
      className={`tw-flex tw-flex-col tw-gap-4 tw-rounded-[28px] tw-border-2 tw-border-solid tw-bg-white tw-p-5 md:tw-p-7 ${
        ours
          ? "tw-border-jam-ink tw-shadow-[6px_6px_0_#D1FE17]"
          : "tw-border-jam-line"
      }`}
    >
      <div className="tw-flex tw-items-start tw-gap-4">
        <span className="jam-display tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-jam-ink tw-text-xl tw-font-extrabold tw-text-jam-lime">
          {rank}
        </span>
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <h3 className="jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-leading-tight tw-text-jam-ink md:tw-text-2xl">
              {pick.name}
            </h3>
            {ours && (
              <span className="tw-rounded-full tw-bg-jam-lime tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-extrabold tw-uppercase tw-tracking-[0.1em] tw-text-jam-ink">
                That&apos;s us
              </span>
            )}
          </div>
          <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-jam-muted">
            Best for {pick.bestFor}
          </p>
        </div>
      </div>
      <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-leading-[1.7] tw-text-[#3d3a4b]">
        {pick.body}
      </p>
      {(pick.pros?.length || pick.cons?.length) && (
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
          {[
            { label: "Good", items: pick.pros ?? [], good: true },
            { label: "Watch out", items: pick.cons ?? [], good: false },
          ]
            .filter((l) => l.items.length)
            .map((l) => (
              <div
                key={l.label}
                className={`tw-rounded-[18px] tw-p-4 ${
                  l.good ? "tw-bg-[#F8FFDD]" : "tw-bg-[#FCFAFF]"
                }`}
              >
                <p className="tw-m-0 tw-mb-2 tw-text-[11px] tw-font-extrabold tw-uppercase tw-tracking-[0.12em] tw-text-jam-muted">
                  {l.label}
                </p>
                <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
                  {l.items.map((t) => (
                    <li
                      key={t}
                      className="tw-flex tw-gap-2 tw-text-sm tw-font-semibold tw-leading-snug tw-text-[#3d3a4b]"
                    >
                      {l.good ? (
                        <Check
                          size={16}
                          strokeWidth={3}
                          className="tw-mt-0.5 tw-shrink-0 tw-text-[#5C8A00]"
                        />
                      ) : (
                        <Minus
                          size={16}
                          strokeWidth={3}
                          className="tw-mt-0.5 tw-shrink-0 tw-text-jam-muted"
                        />
                      )}
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      )}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-dashed tw-border-jam-line tw-pt-4">
        <span className="tw-text-sm tw-font-extrabold tw-text-jam-ink">
          {pick.price}
        </span>
        {ours ? (
          <Link
            href={pick.url}
            className={`${button} tw-bg-jam-lime tw-text-jam-ink`}
          >
            Try Jammable <ArrowRight size={16} strokeWidth={2.6} />
          </Link>
        ) : (
          <a
            href={pick.url}
            target="_blank"
            rel="noopener"
            className={`${button} tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-text-jam-ink hover:tw-border-jam-purple`}
          >
            Visit site <ArrowUpRight size={16} strokeWidth={2.6} />
          </a>
        )}
      </div>
    </li>
  );
}

function Steps({
  heading,
  items,
  mdx,
}: {
  heading: string;
  items: { title: string }[];
  mdx: MDXRemoteSerializeResult[];
}) {
  return (
    <>
      <H2>{heading}</H2>
      <ol className="tw-relative tw-my-6 tw-flex tw-list-none tw-flex-col tw-gap-4 tw-p-0">
        <span
          aria-hidden="true"
          className="tw-absolute tw-bottom-6 tw-left-[21px] tw-top-6 tw-w-0.5 tw-bg-jam-line"
        />
        {items.map((s, i) => (
          <li key={s.title} className="tw-relative tw-flex tw-gap-4">
            <span className="jam-display tw-z-[1] tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-ink tw-bg-jam-lime tw-text-lg tw-font-extrabold tw-text-jam-ink">
              {i + 1}
            </span>
            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1.5 tw-rounded-[22px] tw-bg-jam-lav tw-p-4 md:tw-p-5">
              <h3 className="tw-m-0 tw-text-[17px] tw-font-extrabold tw-text-jam-ink">
                {s.title}
              </h3>
              <div className="tw-text-[15px]">
                {mdx[i] && (
                  <MDXRemote {...mdx[i]} components={STEP_COMPONENTS} />
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}

function Block({
  block,
  mdx,
  verified,
}: {
  block: ContentBlock;
  mdx: MDXRemoteSerializeResult[];
  verified?: string;
}) {
  switch (block.type) {
    case "md":
      return mdx[0] ? (
        <MDXRemote {...mdx[0]} components={MD_COMPONENTS} />
      ) : null;
    case "steps":
      return <Steps heading={block.heading} items={block.items} mdx={mdx} />;
    case "table":
      return (
        <Table
          heading={block.heading}
          table={block.table}
          verified={verified}
        />
      );
    case "picks":
      return (
        <>
          {block.heading && <H2>{block.heading}</H2>}
          <ol className="tw-my-6 tw-flex tw-list-none tw-flex-col tw-gap-4 tw-p-0">
            {block.items.map((p, i) => (
              <PickCard key={p.name} pick={p} rank={i + 1} />
            ))}
          </ol>
        </>
      );
    case "callout":
      return <Callout block={block} mdx={mdx[0]} />;
    case "matrix":
      return <Matrix heading={block.heading} matrix={block.matrix} />;
    case "verdicts":
      return <Verdicts block={block} />;
    case "bars":
      return <Bars block={block} />;
    case "cta":
      return (
        <div className="tw-relative tw-my-10 tw-flex tw-flex-col tw-items-start tw-gap-5 tw-overflow-hidden tw-rounded-[28px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-flex-row md:tw-items-center md:tw-justify-between md:tw-p-8">
          <span
            aria-hidden="true"
            className="tw-absolute -tw-right-10 -tw-top-16 tw-h-48 tw-w-48 tw-rounded-full tw-bg-jam-purple tw-opacity-60 tw-blur-3xl"
          />
          <p className="jam-display tw-relative tw-m-0 tw-max-w-md tw-text-xl tw-font-extrabold tw-leading-snug md:tw-text-2xl">
            {block.text}
          </p>
          <Link
            href={block.href}
            className="tw-relative tw-inline-flex tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-full tw-bg-jam-lime tw-px-6 tw-py-3.5 tw-text-[15px] tw-font-extrabold tw-text-jam-ink tw-no-underline"
          >
            {block.label} <ArrowRight size={17} strokeWidth={2.6} />
          </Link>
        </div>
      );
  }
}

// A page card for "Keep reading" and the /blog and /compare indexes.
export function ContentCardLink({
  card,
  featured = false,
}: {
  card: ContentCard;
  featured?: boolean;
}) {
  return (
    <Link
      href={contentPath(card)}
      className={`tw-group tw-flex tw-h-full tw-gap-4 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-3 tw-text-jam-ink tw-no-underline tw-transition hover:-tw-translate-y-0.5 hover:tw-border-jam-ink hover:tw-shadow-[6px_6px_0_#D1FE17] ${
        featured ? "tw-flex-col md:tw-flex-row md:tw-p-4" : "tw-flex-col"
      }`}
    >
      <CoverArt
        section={card.section}
        slug={card.slug}
        kind={card.kind}
        label={card.coverLabel}
        size={featured ? "hero" : "card"}
        className={featured ? "md:tw-w-[52%] md:tw-shrink-0" : ""}
      />
      <div
        className={`tw-flex tw-flex-1 tw-flex-col tw-gap-2 tw-px-2 tw-pb-2 ${
          featured ? "md:tw-justify-center md:tw-px-4" : ""
        }`}
      >
        <span className="tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.12em] tw-text-jam-purple">
          {SECTION_LABEL[card.section]}
        </span>
        <h3
          className={`jam-display tw-m-0 tw-font-extrabold tw-leading-[1.1] ${
            featured ? "tw-text-[26px] md:tw-text-[38px]" : "tw-text-xl"
          }`}
        >
          {card.h1}
        </h3>
        <p
          className={`tw-m-0 tw-flex-1 tw-font-semibold tw-leading-relaxed tw-text-jam-muted ${
            featured ? "tw-text-[15px] md:tw-text-base" : "tw-text-sm"
          }`}
        >
          {card.metaDescription}
        </p>
        <span className="tw-mt-1 tw-flex tw-items-center tw-justify-between tw-text-xs tw-font-extrabold tw-text-jam-muted">
          Updated {formatDate(card.updated)}
          <span className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-purple tw-transition-colors group-hover:tw-bg-jam-lime group-hover:tw-text-jam-ink">
            <ArrowRight size={17} strokeWidth={2.6} />
          </span>
        </span>
      </div>
    </Link>
  );
}

// A thin lime bar along the top of the window that fills as you read.
function ReadingProgress() {
  const bar = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      if (bar.current)
        bar.current.style.transform = `scaleX(${max > 0 ? el.scrollTop / max : 0})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <span
      aria-hidden="true"
      className="tw-pointer-events-none tw-fixed tw-left-0 tw-top-0 tw-z-[60] tw-h-1 tw-w-full tw-bg-transparent"
    >
      <span
        ref={bar}
        className="tw-block tw-h-full tw-origin-left tw-scale-x-0 tw-bg-jam-lime"
      />
    </span>
  );
}

function Toc({ items }: { items: { label: string; id: string }[] }) {
  return (
    <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-0.5 tw-p-0">
      {items.map((t, i) => (
        <li key={t.id}>
          <a
            href={`#${t.id}`}
            className="tw-flex tw-gap-2.5 tw-rounded-xl tw-px-3 tw-py-2 tw-text-sm tw-font-bold tw-leading-snug tw-text-jam-muted tw-no-underline hover:tw-bg-jam-lav hover:tw-text-jam-ink"
          >
            <span className="tw-w-4 tw-shrink-0 tw-font-extrabold tw-text-jam-purple">
              {i + 1}
            </span>
            {t.label}
          </a>
        </li>
      ))}
    </ol>
  );
}

interface Props {
  page: ContentPage;
  mdx: MDXRemoteSerializeResult[];
  more?: ContentCard[];
}

const ContentArticle = ({ page, mdx, more = [] }: Props) => {
  const path = contentPath(page);
  const canonical = `${SITE_URL}${path}`;
  const sectionUrl = `${SITE_URL}/${page.section}`;
  const sectionLabel = SECTION_LABEL[page.section];
  const toc = outline(page);
  const minutes = readingMinutes(page);
  const cta = page.blocks.find((b) => b.type === "cta");

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.h1,
    description: page.metaDescription,
    datePublished: page.published,
    dateModified: page.updated,
    author: { "@type": "Organization", name: "Jammable Team", url: SITE_URL },
    publisher: PUBLISHER,
    mainEntityOfPage: canonical,
    inLanguage: "en",
  };
  const picks = page.blocks.flatMap((b) => (b.type === "picks" ? b.items : []));
  const itemListLd = picks.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: page.h1,
        itemListElement: picks.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: p.name,
          url: p.url.startsWith("/") ? `${SITE_URL}${p.url}` : p.url,
        })),
      }
    : null;
  const faqLd = page.faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: sectionLabel,
        item: sectionUrl,
      },
      { "@type": "ListItem", position: 3, name: page.h1, item: canonical },
    ],
  };

  // Hand each block its slice of the serialized Markdown.
  const counts = page.blocks.map((b) => blockMd(b).length);
  const slices = counts.map((n, i) => {
    const start = counts.slice(0, i).reduce((a, c) => a + c, 0);
    return mdx.slice(start, start + n);
  });

  return (
    <>
      <NextSeo
        title={page.title}
        description={page.metaDescription}
        canonical={canonical}
        openGraph={{
          title: page.title,
          description: page.metaDescription,
          url: canonical,
          type: "article",
          article: {
            publishedTime: page.published,
            modifiedTime: page.updated,
          },
          locale: "en_US",
          siteName: "Jammable",
          images: [ogImage(`content/${page.section}/${page.slug}`, page.title)],
        }}
      />
      <Head>
        <link
          rel="alternate"
          type="text/markdown"
          href={`${canonical}.md`}
          title="Markdown version"
        />
        {[articleLd, itemListLd, faqLd, breadcrumbLd].map(
          (ld, i) =>
            ld && (
              <script
                key={i}
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLd(ld) }}
              />
            ),
        )}
      </Head>
      <ReadingProgress />
      <main className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-10 tw-bg-white tw-px-4 tw-pb-16 tw-pt-2 tw-text-jam-ink md:tw-gap-14 md:tw-px-8 md:tw-pt-4">
        <section className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-6 tw-rounded-[34px] tw-bg-jam-lav tw-p-4 tw-pt-6 md:tw-rounded-[44px] md:tw-p-6 lg:tw-grid-cols-[1.15fr_1fr] lg:tw-gap-10 lg:tw-p-8 lg:tw-pl-12">
          <div className="tw-flex tw-flex-col tw-gap-5 tw-px-1 lg:tw-py-6">
            <nav
              aria-label="Breadcrumb"
              className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-extrabold tw-text-jam-muted"
            >
              <Link
                href="/"
                className="tw-text-jam-muted tw-no-underline hover:tw-text-jam-purple"
              >
                Jammable
              </Link>
              <ChevronRight size={15} />
              <Link
                href={`/${page.section}`}
                className="tw-text-jam-muted tw-no-underline hover:tw-text-jam-purple"
              >
                {sectionLabel}
              </Link>
            </nav>
            <h1 className="jam-display tw-m-0 tw-text-[38px] tw-font-extrabold tw-leading-[1.02] tw-tracking-[-0.01em] md:tw-text-[58px]">
              {page.h1}
            </h1>
            <p className="tw-m-0 tw-max-w-xl tw-text-base tw-font-semibold tw-leading-relaxed tw-text-jam-muted md:tw-text-lg">
              {page.metaDescription}
            </p>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-font-extrabold">
              <span className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-white tw-py-1 tw-pl-1 tw-pr-3.5">
                <span className="jam-display tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-ink tw-text-[13px] tw-text-jam-lime">
                  J
                </span>
                Jammable team
              </span>
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white tw-px-3.5 tw-py-2">
                <Clock
                  size={15}
                  strokeWidth={2.6}
                  className="tw-text-jam-purple"
                />
                {minutes} min read
              </span>
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white tw-px-3.5 tw-py-2">
                <CalendarDays
                  size={15}
                  strokeWidth={2.6}
                  className="tw-text-jam-purple"
                />
                Published{" "}
                <time dateTime={page.published}>
                  {formatDate(page.published)}
                </time>
              </span>
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white tw-px-3.5 tw-py-2">
                <CalendarDays
                  size={15}
                  strokeWidth={2.6}
                  className="tw-text-jam-purple"
                />
                Updated{" "}
                <time dateTime={page.updated}>{formatDate(page.updated)}</time>
              </span>
            </div>
          </div>
          <CoverArt
            section={page.section}
            slug={page.slug}
            kind={page.kind}
            label={page.coverLabel}
            size="hero"
            // Phones go straight from the title to the answer.
            className="tw-hidden md:tw-flex"
          />
        </section>

        <section
          className={`tw-mx-auto tw-grid tw-w-full tw-max-w-[1100px] tw-grid-cols-1 tw-gap-4 ${
            page.stats?.length ? "lg:tw-grid-cols-[1.6fr_1fr]" : ""
          }`}
        >
          <div className="tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-ink tw-bg-white tw-p-6 tw-shadow-[6px_6px_0_#D1FE17] md:tw-p-8">
            <span className="tw-inline-block tw-rounded-full tw-bg-jam-lime tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.12em]">
              Short answer
            </span>
            {page.question && (
              <h2 className="jam-display tw-m-0 tw-mt-4 tw-text-[22px] tw-font-extrabold tw-leading-tight md:tw-text-[26px]">
                {page.question}
              </h2>
            )}
            <p className="tw-m-0 tw-mt-4 tw-text-[17px] tw-font-bold tw-leading-[1.65] md:tw-text-[19px]">
              {page.answer}
            </p>
            {page.takeaways && page.takeaways.length > 0 && (
              <ul className="tw-m-0 tw-mt-5 tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-border-0 tw-border-t tw-border-dashed tw-border-jam-line tw-p-0 tw-pt-5">
                {page.takeaways.map((t) => (
                  <li
                    key={t}
                    className="tw-flex tw-gap-3 tw-text-[15px] tw-font-semibold tw-leading-snug tw-text-[#3d3a4b]"
                  >
                    <span className="tw-mt-px tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
                      <Check size={13} strokeWidth={3.2} />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {page.stats && page.stats.length > 0 && (
            <ul className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-2.5 tw-p-0 md:tw-grid-cols-3 md:tw-gap-3 lg:tw-grid-cols-1">
              {page.stats.map((s) => (
                <li
                  key={s.label}
                  className="tw-flex tw-items-center tw-gap-4 tw-rounded-[20px] tw-bg-jam-ink tw-px-5 tw-py-3.5 tw-text-white md:tw-flex-col md:tw-items-start md:tw-justify-center md:tw-gap-1.5 md:tw-p-5 lg:tw-flex-row lg:tw-items-center lg:tw-gap-4 lg:tw-px-6"
                >
                  <span className="jam-display tw-min-w-[88px] tw-whitespace-nowrap tw-text-[26px] tw-font-extrabold tw-leading-none tw-text-jam-lime md:tw-min-w-0 md:tw-text-[30px] lg:tw-min-w-[110px]">
                    {s.value}
                  </span>
                  <span className="tw-text-xs tw-font-bold tw-leading-snug tw-text-white/75 md:tw-text-sm">
                    {s.label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="tw-mx-auto tw-grid tw-w-full tw-max-w-[1100px] tw-grid-cols-1 tw-gap-10 lg:tw-grid-cols-[240px_minmax(0,1fr)] lg:tw-gap-14">
          <aside className="tw-hidden lg:tw-block">
            <div className="tw-sticky tw-top-24 tw-flex tw-flex-col tw-gap-4">
              <nav aria-label="On this page">
                <p className="tw-m-0 tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-px-3 tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.12em] tw-text-jam-ink">
                  <ListOrdered size={15} strokeWidth={2.6} />
                  On this page
                </p>
                <Toc items={toc} />
              </nav>
              {cta?.type === "cta" && (
                <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[22px] tw-bg-jam-ink tw-p-5 tw-text-white">
                  <p className="tw-m-0 tw-text-sm tw-font-extrabold tw-leading-snug">
                    {cta.text}
                  </p>
                  <Link
                    href={cta.href}
                    className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-bg-jam-lime tw-px-4 tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline"
                  >
                    {cta.label} <ArrowRight size={15} strokeWidth={2.6} />
                  </Link>
                </div>
              )}
            </div>
          </aside>

          <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-12">
            <details className="tw-group tw-rounded-[22px] tw-bg-jam-lav tw-p-1 lg:tw-hidden">
              <summary className="tw-flex tw-cursor-pointer tw-list-none tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-text-sm tw-font-extrabold tw-text-jam-ink [&::-webkit-details-marker]:tw-hidden">
                <span className="tw-flex tw-items-center tw-gap-2">
                  <ListOrdered size={16} strokeWidth={2.6} />
                  On this page
                </span>
                <ChevronRight
                  size={18}
                  strokeWidth={2.6}
                  className="tw-transition-transform group-open:tw-rotate-90"
                />
              </summary>
              <div className="tw-px-1 tw-pb-2">
                <Toc items={toc} />
              </div>
            </details>

            <article className="tw-w-full tw-max-w-[720px] tw-text-[16px] tw-font-semibold tw-text-[#3d3a4b] md:tw-text-[17px]">
              {page.blocks.map((b, i) => (
                <Block
                  key={i}
                  block={b}
                  mdx={slices[i]}
                  verified={page.verified}
                />
              ))}
            </article>

            {page.faqs.length > 0 && (
              <section className="tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-2">
                <h2
                  id={FAQ_ID}
                  className="jam-display tw-m-0 tw-mb-2 tw-scroll-mt-24 tw-text-[28px] tw-font-extrabold tw-leading-[1.1] md:tw-text-[34px]"
                >
                  Questions people ask
                </h2>
                <Accordion
                  type="single"
                  collapsible
                  className="tw-flex tw-w-full tw-flex-col tw-gap-2 tw-rounded-[28px] tw-bg-jam-lav tw-p-2 md:tw-p-3"
                >
                  {page.faqs.map((faq, i) => (
                    <AccordionItem
                      key={faq.q}
                      value={`faq-${i}`}
                      className="tw-rounded-[20px] tw-border-0 tw-bg-white tw-px-5"
                    >
                      <AccordionTrigger className="tw-text-left tw-text-base tw-font-extrabold tw-text-jam-ink">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="tw-text-[15px] tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            )}

            {page.sources && page.sources.length > 0 && (
              <section className="tw-w-full tw-max-w-[720px]">
                <h2 className="tw-m-0 tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.12em] tw-text-jam-muted">
                  Sources
                </h2>
                <ul className="tw-m-0 tw-mt-3 tw-flex tw-list-none tw-flex-wrap tw-gap-2 tw-p-0 tw-text-xs tw-font-extrabold">
                  {page.sources.map((s) => (
                    <li key={s.href}>
                      <a
                        href={s.href}
                        target="_blank"
                        rel="noopener nofollow"
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-solid tw-border-jam-line tw-px-3 tw-py-1.5 tw-text-jam-muted tw-no-underline hover:tw-border-jam-purple hover:tw-text-jam-purple"
                      >
                        {s.label}
                        <ArrowUpRight size={12} strokeWidth={2.6} />
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>

        {more.length > 0 && (
          <section className="tw-flex tw-flex-col tw-gap-5">
            <h2 className="jam-display tw-m-0 tw-text-[28px] tw-font-extrabold md:tw-text-[34px]">
              Keep reading
            </h2>
            <ul
              className={`tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-4 tw-p-0 ${
                more.length > 1 ? "md:tw-grid-cols-2 lg:tw-grid-cols-3" : ""
              }`}
            >
              {more.map((c) => (
                <li key={`${c.section}/${c.slug}`}>
                  {/* One card alone gets the wide layout. */}
                  <ContentCardLink card={c} featured={more.length === 1} />
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="tw-flex tw-flex-col tw-gap-4 tw-rounded-[34px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-rounded-[44px] md:tw-p-10">
          <h2 className="jam-display tw-m-0 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
            Keep exploring
          </h2>
          <div className="tw-grid tw-grid-cols-1 tw-gap-2.5 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
            {page.related.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-[22px] tw-bg-jam-deep tw-px-[18px] tw-py-4 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline hover:tw-text-jam-lime"
              >
                {link.label}
                <ChevronRight size={18} strokeWidth={2.6} />
              </Link>
            ))}
          </div>
        </section>
      </main>
    </>
  );
};

export default ContentArticle;
